import Noty from 'noty';

import {
  MediaPermissionsError,
  MediaPermissionsErrorType,
  requestMediaPermissions
} from 'mic-check';

import {
  showNode,
  hideNode,
  isSafari
} from '../utils';

import {
  saveFileLocally,
  loadFileLocally,
  deleteFileLocally
} from '../indexed-db';

import {
  recorderMimeType,
  createMicMonitor,
  isAudible,
  confirmSilentTake,
  createMicCheck,
  audioTrackState
} from '../recording-utils';

'use strict';

/* globals MediaRecorder */

document.addEventListener("turbolinks:load", function () {
    let stream;
    let mediaRecorder;
    let recordedBlobs = [];

    const errorMessage        = document.querySelector('span#errorMsg');
    const recordedVideo       = document.querySelector('video#recorded');
    const recordButton        = document.querySelector('button#record');
    const acceptButton        = document.querySelector('button#download');
    const deleteButton        = document.querySelector('button#delete');
    const startButton         = document.querySelector('button#start');
    const selectionButtons    = document.querySelector('#selection-buttons');
    const recordingButtons    = document.querySelector('#recording-buttons');
    const submitButton        = document.querySelector('#submit-button');
    const videoRecorderModal  = document.querySelector('#videoRecorder');
    const videoLoader         = document.querySelector('#video-loader');
    const videoSelectedModal  = document.querySelector('#videoSelectedModal');
    const submitFromModalButton = document.querySelector('#submit-from-modal');
    const videoConfirmation = document.querySelector('#video-confirm');
    const jobApplicationForm = document.querySelector('#job-application-form');
    
    const playbackBar = document.querySelector('#record-playback');
    const playToggle = document.querySelector('#record-play-toggle');
    const scrubber = document.querySelector('#record-scrubber');
    const playbackTime = document.querySelector('#record-playback-time');
    const micMeter = document.querySelector('#mic-meter');
    const micSilentWarning = document.querySelector('#mic-silent-warning');

    let micMonitor = null;
    let takeWasSilent = false;
    let heardAudioDuringTake = false;
    let isCapturingTake = false;
    let micConfirmed = false;
    let micCheck = null;
    let modalClosing = false;
    let previewGeneration = 0;

    const modalIsShown = () =>
      !!(videoRecorderModal && videoRecorderModal.classList.contains('show'));

    const syncPlaybackUi = () => {
      if (!recordedVideo) return;
      if (playToggle) {
        playToggle.textContent = recordedVideo.paused ? 'Play' : 'Pause';
      }
      if (scrubber && recordedVideo.duration) {
        scrubber.value = (recordedVideo.currentTime / recordedVideo.duration) * 100;
      }
      if (playbackTime) {
        playbackTime.textContent = formatClock((recordedVideo.currentTime || 0) * 1000);
      }
    };

    const setReviewMode = (on) => {
      const title = document.querySelector('#record-modal-title');
      const hint = document.querySelector('#record-review-hint');
      const limitNote = document.querySelector('#record-limit-note');
      const badge = document.querySelector('#record-review-badge');

      if (title) title.textContent = on ? 'Review your video' : 'Record Your Video';
      if (hint) hint.classList.toggle('hidden', !on);
      if (limitNote) limitNote.classList.toggle('hidden', on);
      if (badge) badge.hidden = !on;
      if (playbackBar) playbackBar.hidden = !on;
      if (micMeter) micMeter.hidden = on || micConfirmed;
      if (micSilentWarning) micSilentWarning.hidden = !(on && takeWasSilent);
      if (on && recordedVideo && !modalClosing && modalIsShown()) {
        recordedVideo.play().catch(() => {});
        syncPlaybackUi();
      }
    };

    const stopMicMonitor = () => {
      if (micMonitor) {
        micMonitor.stop();
        micMonitor = null;
      }
    };

    const setMicPrompt = (text) => {
      const label = micMeter && micMeter.querySelector('.mic-meter__label');
      if (label && label.textContent !== text) label.textContent = text;
    };

    const handleMicLevel = (level) => {
      if (isCapturingTake && isAudible(level.rms, level)) {
        heardAudioDuringTake = true;
      }
      if (micConfirmed || !micCheck) return;

      const status = micCheck.update(level.rms, level);
      if (micMeter && !micMeter.hidden) setMicPrompt(status.label);
      if (status.hide) {
        micConfirmed = true;
        if (micMeter) micMeter.hidden = true;
      }
    };

    const startMicMonitor = (mediaStream) => {
      stopMicMonitor();
      if (!mediaStream) return;

      micConfirmed = false;
      micCheck = createMicCheck();
      const trackState = audioTrackState(mediaStream);
      if (!trackState.hasAudioTrack && errorMessage) {
        errorMessage.innerHTML = 'We could not access your microphone. Please check your browser settings and try again.';
      }

      if (micMeter) {
        micMeter.hidden = false;
        setMicPrompt(
          !trackState.hasAudioTrack ? 'No microphone connected'
            : trackState.muted ? 'Microphone is muted'
            : 'Checking your mic…'
        );
      }

      micMonitor = createMicMonitor(mediaStream, handleMicLevel);
      micMonitor.resume();
    };

    const processDeleteVideo = (event) => {
      event.preventDefault();

      if (!confirm('Delete this take and record again?')) {
        return;
      }

      hideNode(selectionButtons);
      showNode(recordingButtons);
      setReviewMode(false);

      resetRecordedVideo();
      hideNode(recordedVideo);

      if (!streamIsLive()) initRecording();
    };

    const processAcceptVideo = (event) => {
      event.preventDefault();

      if (takeWasSilent && !confirmSilentTake()) {
        return;
      }

      closeModal(videoRecorderModal);
      
      hideNode(recordButton);
      showNode(deleteButton);
      showNode(acceptButton);
      showNode(submitButton);

      recordedVideo.pause();

      FileValidator.fileChosenSuccess(getBlobsUrl())

      let mimeType = (isSafari())? 'video/mp4' : 'video/webm'
      const blob = new Blob(recordedBlobs, {type: mimeType})
      saveFileLocally('jobApplication', blob)
    };

    const getBlobsUrl = () => {
        let mimeType = (isSafari()) ? 'video/mp4' : 'video/webm'
        return window.URL.createObjectURL(new Blob(recordedBlobs, {type: mimeType}));
    };

    const resetRecordedVideo = () => {
      if (recordedVideo == null) {
        return;
      }
      
      recordedBlobs = [];
      deleteFileLocally('jobApplication');

      recordedVideo.pause();
      recordedVideo.removeAttribute('src');
      recordedVideo.load();
      
      document.querySelector('video#stream').style.visibility = 'visible';
      document.querySelector('video#recorded').style.visibility = 'hidden';
      takeWasSilent = false;
      heardAudioDuringTake = false;
      if (micSilentWarning) micSilentWarning.hidden = true;
      if (micMeter) micMeter.hidden = micConfirmed;
    };

    const handleDataAvailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedBlobs.push(event.data);
        }
    };

    const closeModal = (modal) => {
        $(modal).modal('hide');
    };

    const updateBlob = (blob) => {
        let newBlob = new Blob([blob], {type: blob.type});

        newBlob.lastModifiedDate = new Date();
        newBlob.name = "video.webm";

        return newBlob;
    };
    
    let uploadTimer; // Timer for upload timeout
    const uploadTimeoutDuration = 60000; // 60 seconds in milliseconds

    const startUploadTimer = () => {
      uploadTimer = setTimeout(() => {
        if (submitButton.disabled) {
          submitButton.disabled = false;
          // Optionally, hide the progress bar or update UI
          // Example: document.getElementById('progressBar').style.display = 'none';
          new Noty({
             type: 'error',
             layout: 'topCenter',
             theme: 'nest',
             text: "<p>Your video is taking longer than usual to upload...</p><p>You can keep this page open or try submitting again below.</p> <p>Your first attempt will continue to upload in case it finishes first.</p>",
             timeout: false,
             progressBar: false,
             closeWith: ['click', 'button'],
             killer: true,
             animation: {
                 open: 'animated bounceInDown', // Animate.css class names
                 close: 'animated bounceOutUp' // Animate.css class names
               }
          }).show();
        }
      }, uploadTimeoutDuration);
    };

    const attachSignedIdAndSubmit = (input, signedId) => {
      if (!input || !jobApplicationForm) return;

      const form = input.form || jobApplicationForm;
      form.querySelectorAll(`input[type="hidden"][name="${input.name}"]`).forEach((el) => el.remove());

      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = input.name;
      hiddenField.value = signedId;
      input.insertAdjacentElement('beforebegin', hiddenField);
      input.disabled = true;
      jobApplicationForm.submit();
    };

    const uploadFile = () => {
      if (submitButton.classList.contains('in-demo')) {
        return;
      }

      if (recordedBlobs.length > 0) {
        submitButton.disabled = true;

        let mimeType = (isSafari())? 'video/mp4' : 'video/webm'
        const blob = new Blob(recordedBlobs, {type: mimeType});

        const input = document.querySelector('input.video-file') || document.querySelector('input[type=file]');

        const url = input.dataset.directUploadUrl;
        const upload = new ActiveStorage.DirectUpload(updateBlob(blob), url, new Uploader);
        
        // Start the upload timeout timer
        startUploadTimer();
        
        upload.create((error, blob) => {
          console.log('Upload completed...')
          // Clear the timeout timer when the upload is complete or fails
          clearTimeout(uploadTimer);
          
          if (error) {
            submitButton.disabled = false;

            new Noty({
               type: 'error',
               layout: 'topCenter',
               theme: 'nest',
               text: "Your video could not be uploaded, please try again.",
               timeout: '4000',
               progressBar: true,
               closeWith: ['click', 'button'],
               killer: true,
               animation: {
                   open: 'animated bounceInDown', // Animate.css class names
                   close: 'animated bounceOutUp' // Animate.css class names
                 }
            }).show();
            
            Rollbar.critical(error);
          } else {
            let applicant = $('#applicant_email').val();
            
            Rollbar.info(`${applicant} submitted a video`);
            attachSignedIdAndSubmit(input, blob.signed_id);
          }
        })
      }
    };

    class Uploader {
      directUploadWillStoreFileWithXHR(request) {
        request.upload.addEventListener("progress", event => this.directUploadDidProgress(event));

        submitButton.insertAdjacentHTML("afterend", `
            <div id="direct-upload" class="direct-upload direct-upload--pending mb-0">
              <div id="direct-upload-progress" class="direct-upload__progress" style="width: 0%"></div>
              <span class="direct-upload__filename" id="progress-text">Uploading: 0%</span>
            </div>
        `)
      }

      directUploadDidProgress(event) {
        const progress = (event.loaded / event.total) * 100.0;

        const progressElement = document.getElementById(`direct-upload-progress`);
        const progressElementSpan = document.getElementById(`progress-text`);

        const element = document.getElementById(`direct-upload`);
        element.classList.remove("direct-upload--pending");

        progressElement.style.width = `${progress}%`;
        progressElementSpan.innerHTML = `Uploading: ${~~progress}%`
      }
    }

    const MAX_RECORDING_MS = 5 * 60 * 1000;
    const recordStatus = document.querySelector('#record-status');
    const recordElapsed = document.querySelector('#record-elapsed');
    let recordingStartedAt = null;
    let recordingTimerId = null;

    const formatClock = (ms) => {
      const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    };

    const clearRecordingTimer = () => {
      if (recordingTimerId) {
        clearInterval(recordingTimerId);
        recordingTimerId = null;
      }
      recordingStartedAt = null;
      if (recordStatus) {
        recordStatus.hidden = true;
        recordStatus.classList.remove('is-warning');
      }
      if (recordButton) {
        recordButton.classList.remove('btn-danger', 'is-recording');
        recordButton.classList.add('btn-success');
      }
    };

    const tickRecordingTimer = () => {
      const elapsed = Date.now() - recordingStartedAt;
      const remaining = MAX_RECORDING_MS - elapsed;
      if (recordElapsed) {
        recordElapsed.textContent = formatClock(elapsed);
      }
      if (recordStatus) {
        recordStatus.classList.toggle('is-warning', remaining <= 30000);
      }
      if (remaining <= 0) {
        stopRecording();
      }
    };

    const pauseModalMedia = () => {
      const streamVideo = document.querySelector('video#stream');
      if (streamVideo) {
        streamVideo.pause();
      }
      if (recordedVideo) {
        recordedVideo.pause();
        syncPlaybackUi();
      }
    };

    const stopStream = () => {
      if (!stream) return;
      stream.getTracks().forEach(track => track.stop());
      stream = null;
    };

    const stopTracks = () => {
      // Invalidate an in-flight getUserMedia so the camera cannot start after close.
      previewGeneration += 1;
      modalClosing = true;
      // #recorded is autoplay. A take finished while the modal is closing would
      // otherwise start playing (with sound) once the blob loads.
      if (recordedVideo) recordedVideo.autoplay = false;

      if (isRecording()) {
          stopRecording();
      }

      stopMicMonitor();
      stopStream();

      const streamVideo = document.querySelector('video#stream');
      if (streamVideo) {
        streamVideo.pause();
        streamVideo.srcObject = null;
      }

      pauseModalMedia();
    };

    const startRecording = () => {
      const timeSlice = 10;

      hideNode(recordedVideo);
      try {
          mediaRecorder = new MediaRecorder(stream, recorderMimeType());
      } catch (e) {
          Rollbar.critical(e);
          errorMessage.innerHTML = `Exception while creating MediaRecorder: ${JSON.stringify(e)}`;
          return;
      }

      recordButton.textContent = 'Stop Recording';
      recordButton.classList.remove('btn-success');
      recordButton.classList.add('btn-danger', 'is-recording');
      if (recordStatus) {
        recordStatus.hidden = false;
        recordStatus.classList.remove('is-warning');
      }
      if (recordElapsed) {
        recordElapsed.textContent = '0:00';
      }
      if (micMonitor) micMonitor.resume();
      heardAudioDuringTake = false;
      takeWasSilent = false;
      isCapturingTake = true;
      recordingStartedAt = Date.now();
      recordingTimerId = setInterval(tickRecordingTimer, 200);

      mediaRecorder.ondataavailable = handleDataAvailable;
      mediaRecorder.start(timeSlice);

      document.querySelector('video#stream').style.visibility = 'visible'
      document.querySelector('video#recorded').style.visibility = 'hidden'
    };

    const stopRecording = () => {
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        return;
      }

      clearRecordingTimer();
      isCapturingTake = false;
      takeWasSilent = !heardAudioDuringTake;
      hideNode(recordingButtons);

      showNode(deleteButton);
      showNode(acceptButton);
      showNode(recordedVideo);

      if (!modalClosing) recordedVideo.autoplay = true;
      recordedVideo.src = getBlobsUrl();
      mediaRecorder.stop();

      selectionButtons.style.display = "flex";
      recordButton.textContent = 'Start Recording';
      setReviewMode(true);
      document.querySelector('video#stream').style.visibility = 'hidden'
      document.querySelector('video#recorded').style.visibility = 'visible'
    };

    const processRecording = (event) => {
        event.preventDefault();
        if (recordButton.textContent === 'Start Recording') {
            startRecording();
        } else {
            stopRecording();
        }
    };

    const showVideoLoader = () => {
      if (videoLoader) videoLoader.classList.remove('is-hidden');
    };

    const hideVideoLoader = () => {
      if (videoLoader) videoLoader.classList.add('is-hidden');
    };

    let loaderWatchId = 0;

    const hideLoaderWhenFrameReady = (video) => {
      if (!video) return;

      const watchId = ++loaderWatchId;
      const finish = () => {
        if (watchId !== loaderWatchId) return;
        hideVideoLoader();
        video.removeEventListener('loadeddata', finish);
        video.removeEventListener('playing', finish);
        video.removeEventListener('resize', finish);
      };

      video.addEventListener('loadeddata', finish);
      video.addEventListener('playing', finish);
      video.addEventListener('resize', finish);

      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(() => finish());
      }

      let frames = 0;
      const poll = () => {
        if (watchId !== loaderWatchId) return;
        if (video.videoWidth > 0 && video.readyState >= 2) {
          finish();
          return;
        }
        frames += 1;
        if (frames < 240) requestAnimationFrame(poll);
      };
      requestAnimationFrame(poll);
    };

    const startStreaming = (mediaStream) => {
        const streamVideo = document.querySelector('video#stream');
        if (!streamVideo) return;

        streamVideo.srcObject = mediaStream;
        hideLoaderWhenFrameReady(streamVideo);
    };

    const isRecording = () => {
        if (mediaRecorder) {
            return mediaRecorder.state === 'recording';
        } else {
            return false
        }
    };

    const hasRecordedTake = () => {
      if (!recordedVideo) return false;
      const src = recordedVideo.getAttribute('src');
      return !!(src && recordedVideo.style.display !== 'none');
    };

    const streamIsLive = () =>
      !!(stream && stream.getTracks().some(track => track.readyState === 'live'));

    async function initRecording() {
        if (hasRecordedTake()) {
          if (micMeter) micMeter.hidden = true;
          return;
        }

        const generation = ++previewGeneration;
        modalClosing = false;
        const constraints = {
            audio: { echoCancellation: false },
            video: { width: 1280, height: 720 }
        };

        showVideoLoader();

        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            if (generation !== previewGeneration) {
                mediaStream.getTracks().forEach(track => track.stop());
                return;
            }
            stream = mediaStream;
            startStreaming(stream);
            startMicMonitor(stream);
            showNode(recordButton)
        } catch (e) {
            hideVideoLoader();
            let userFriendlyMessage = '';
            const errorString = e.toString();
            
            if (errorString.includes('NotAllowedError') || errorString.includes('Permission denied') || errorString.includes('not allowed by the user agent')) {
                userFriendlyMessage = `
                    <div style="text-align: left;">
                        <strong>Camera & Microphone Access Needed</strong>
                        <p style="margin: 8px 0 0 0; font-size: 14px;">VideeBop needs access to your camera and microphone to record your video response.</p>
                        <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>How to fix this:</strong></p>
                        <ol style="margin: 8px 0 0 0; padding-left: 20px; font-size: 14px;">
                            <li>Look for a camera/microphone icon in your browser's address bar</li>
                            <li>Click it and select "Allow" or "Always allow"</li>
                            <li>Refresh this page and try again</li>
                        </ol>
                        <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>On mobile:</strong> Check your device settings to ensure the browser has permission to use the camera and microphone.</p>
                    </div>
                `;
            } else if (errorString.includes('NotFoundError') || errorString.includes('not found')) {
                userFriendlyMessage = 'Camera or microphone not found. Please make sure they are connected and try again.';
            } else if (errorString.includes('NotReadableError') || errorString.includes('in use')) {
                userFriendlyMessage = 'Your camera or microphone is being used by another app. Please close other apps (like Zoom, Skype, or video calls) and try again.';
            } else {
                userFriendlyMessage = `
                    <div style="text-align: left;">
                        <strong>Unable to Access Camera</strong>
                        <p style="margin: 8px 0 0 0; font-size: 14px;">We're having trouble accessing your camera or microphone.</p>
                        <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>Try these steps:</strong></p>
                        <ol style="margin: 8px 0 0 0; padding-left: 20px; font-size: 14px;">
                            <li>Make sure your camera and microphone are connected and working</li>
                            <li>Check your browser settings to allow camera/microphone access</li>
                            <li>Close other apps that might be using your camera</li>
                            <li>Refresh the page and try again</li>
                        </ol>
                    </div>
                `;
                Rollbar.critical(e);
            }
            
            if (errorMessage) {
                errorMessage.innerHTML = userFriendlyMessage;
            }
            
            // Also show in mic-permission-info if it exists
            const micPermissionNode = document.querySelector('#mic-permission-info');
            if (micPermissionNode) {
                micPermissionNode.classList.remove('hidden');
                micPermissionNode.innerHTML = userFriendlyMessage;
            }
            
            console.error('navigator.getUserMedia error:', e.toString());
        }
    };
    
    $(videoRecorderModal).on('hide.bs.modal', stopTracks);
    $(videoRecorderModal).on('hidden.bs.modal', pauseModalMedia);
    $(videoRecorderModal).on('shown.bs.modal', () => {
      modalClosing = false;
    });

    if (videoRecorderModal) {
        startButton.addEventListener('click', async () => await initRecording());
        recordButton.addEventListener('click', processRecording);
        deleteButton.addEventListener('click', processDeleteVideo);
        acceptButton.addEventListener('click', processAcceptVideo);
    }

    if (recordedVideo) {
      recordedVideo.addEventListener('timeupdate', syncPlaybackUi);
      recordedVideo.addEventListener('play', () => {
        if (modalClosing || !modalIsShown()) {
          recordedVideo.pause();
          return;
        }
        syncPlaybackUi();
      });
      recordedVideo.addEventListener('pause', syncPlaybackUi);
    }
    if (playToggle) {
      playToggle.addEventListener('click', () => {
        if (recordedVideo.paused) {
          recordedVideo.play();
        } else {
          recordedVideo.pause();
        }
      });
    }
    if (scrubber) {
      scrubber.addEventListener('input', () => {
        if (recordedVideo.duration) {
          recordedVideo.currentTime = (Number(scrubber.value) / 100) * recordedVideo.duration;
        }
      });
    }

    const pagePreviewVideo = document.querySelector('#video-preview');
    const pagePlayToggle = document.querySelector('#page-play-toggle');
    const pageScrubber = document.querySelector('#page-scrubber');
    const pagePlaybackTime = document.querySelector('#page-playback-time');

    const syncPagePlaybackUi = () => {
      if (!pagePreviewVideo) return;
      if (pagePlayToggle) {
        pagePlayToggle.textContent = pagePreviewVideo.paused ? 'Play' : 'Pause';
      }
      if (pageScrubber && pagePreviewVideo.duration) {
        pageScrubber.value = (pagePreviewVideo.currentTime / pagePreviewVideo.duration) * 100;
      }
      if (pagePlaybackTime) {
        pagePlaybackTime.textContent = formatClock((pagePreviewVideo.currentTime || 0) * 1000);
      }
    };

    if (pagePreviewVideo) {
      pagePreviewVideo.addEventListener('timeupdate', syncPagePlaybackUi);
      pagePreviewVideo.addEventListener('play', syncPagePlaybackUi);
      pagePreviewVideo.addEventListener('pause', syncPagePlaybackUi);
    }
    if (pagePlayToggle) {
      pagePlayToggle.addEventListener('click', () => {
        if (pagePreviewVideo.paused) {
          pagePreviewVideo.play();
        } else {
          pagePreviewVideo.pause();
        }
      });
    }
    if (pageScrubber) {
      pageScrubber.addEventListener('input', () => {
        if (pagePreviewVideo.duration) {
          pagePreviewVideo.currentTime = (Number(pageScrubber.value) / 100) * pagePreviewVideo.duration;
        }
      });
    }
    
    if (jobApplicationForm) {
      $(jobApplicationForm).one('submit', (event) => {
        event.preventDefault();
        uploadFile();
      });
    }
    
    if (submitFromModalButton) {
      submitFromModalButton.addEventListener('click', () => {
          if (recordedBlobs.length > 0) {
              submitFromModalButton.type = "button";
              uploadFile();
          } else {
              submitFromModalButton.type = "submit";
          }
      });
    }

    var $videoFile = $('.video-file');
    var $jobApplicationFormInputs = $('#applicant_name, #applicant_email, #applicant_zip_code, #applicant_phone_number, #applicant_job_applications_fun_fact');
    var $recordingInfo = $('#recording-info');
    var $recordingTips = $('#recording-tips');
    var $questions = $('#questions');
    var $resubmitButton = $('#resubmitButton');
    var $submitButton = $('#submit-button');
    var $recordBtn = $('#start');
    var $jobApplicationVideoInput = $('#applicant_job_applications_video');
    var $uploadButton = $('#upload-video');
    var $confirmationCheckbox = $('#confirmation');

    requestMediaPermissions()
    .then(() => {
      // can successfully access camera and microphone streams
      // DO SOMETHING HERE      
      let node = document.querySelectorAll('#mic-permission-info')[0]

      if (node) {
        node.classList.add('hidden')
      }
    })
    .catch((err) => {
      $recordBtn.hide();
      
      const { type, name, message } = err;

      // set the error message if not handled by cases below
      let nodeMsg = message;

      if (type === MediaPermissionsErrorType.SystemPermissionDenied) {
        // browser does not have permission to access camera or microphone
        nodeMsg = `
          <div style="text-align: left;">
            <strong>Browser Permission Issue</strong>
            <p style="margin: 8px 0 0 0; font-size: 14px;">Your browser is blocking access to the camera or microphone.</p>
            <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>How to fix:</strong></p>
            <ol style="margin: 8px 0 0 0; padding-left: 20px; font-size: 14px;">
              <li>Go to your browser settings (usually in the address bar or browser menu)</li>
              <li>Find "Site settings" or "Permissions"</li>
              <li>Allow camera and microphone access for videebop.com</li>
              <li>Refresh this page and try again</li>
            </ol>
          </div>
        `;
      } else if (type === MediaPermissionsErrorType.UserPermissionDenied) {
        // user didn't allow app to access camera or microphone
        nodeMsg = `
          <div style="text-align: left;">
            <strong>Camera & Microphone Access Needed</strong>
            <p style="margin: 8px 0 0 0; font-size: 14px;">VideeBop needs access to your camera and microphone to record your video response.</p>
            <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>How to fix this:</strong></p>
            <ol style="margin: 8px 0 0 0; padding-left: 20px; font-size: 14px;">
              <li>Look for a camera/microphone icon in your browser's address bar</li>
              <li>Click it and select "Allow" or "Always allow"</li>
              <li>Refresh this page and try again</li>
            </ol>
            <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>On mobile:</strong> Check your device settings to ensure the browser has permission to use the camera and microphone.</p>
          </div>
        `;
      } else if (type === MediaPermissionsErrorType.CouldNotStartVideoSource) {
        // camera is in use by another application (Zoom, Skype) or browser tab (Google Meet, Messenger Video)
        // (mostly Windows specific problem)
        nodeMsg = `
          <div style="text-align: left;">
            <strong>Camera or Microphone in Use</strong>
            <p style="margin: 8px 0 0 0; font-size: 14px;">Your camera or microphone is being used by another app.</p>
            <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>How to fix:</strong></p>
            <ol style="margin: 8px 0 0 0; padding-left: 20px; font-size: 14px;">
              <li>Close other apps that might be using your camera (Zoom, Skype, Teams, etc.)</li>
              <li>Close other browser tabs with video calls (Google Meet, Messenger, etc.)</li>
              <li>Refresh this page and try again</li>
            </ol>
          </div>
        `;
      } else if (message.includes('not found')) {
        nodeMsg = `
          <div style="text-align: left;">
            <strong>Camera or Microphone Not Found</strong>
            <p style="margin: 8px 0 0 0; font-size: 14px;">We couldn't find your camera or microphone.</p>
            <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>How to fix:</strong></p>
            <ol style="margin: 8px 0 0 0; padding-left: 20px; font-size: 14px;">
              <li>Make sure your camera and microphone are connected and turned on</li>
              <li>Check that they're not being used by another app</li>
              <li>Try unplugging and reconnecting them</li>
              <li>Refresh the page and try again</li>
            </ol>
          </div>
        `;
      } else {
        // Handle generic permission errors with user-friendly message
        if (message.includes('not allowed by the user agent') || message.includes('Permission denied') || message.includes('NotAllowedError')) {
          nodeMsg = `
            <div style="text-align: left;">
              <strong>Camera & Microphone Access Needed</strong>
              <p style="margin: 8px 0 0 0; font-size: 14px;">VideeBop needs access to your camera and microphone to record your video response.</p>
              <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>How to fix this:</strong></p>
              <ol style="margin: 8px 0 0 0; padding-left: 20px; font-size: 14px;">
                <li>Look for a camera/microphone icon in your browser's address bar</li>
                <li>Click it and select "Allow" or "Always allow"</li>
                <li>Refresh this page and try again</li>
              </ol>
              <p style="margin: 8px 0 0 0; font-size: 14px;"><strong>On mobile:</strong> Check your device settings to ensure the browser has permission to use the camera and microphone.</p>
            </div>
          `;
        } else {
          // not all error types are handled by this library, lets record those
          Rollbar.critical(err)
        }
      }

      let node = document.querySelectorAll('#mic-permission-info')[0]
      
      if (node) {
        node.classList.remove('hidden')
        node.innerHTML = nodeMsg
      }      
    });

    if ($videoFile) {
        $($videoFile).on('change', function () {
            var fileName = this.files[0].name;

            if (FileValidator.isVideo(fileName)) {
                FileValidator.fileChosenSuccess(URL.createObjectURL(this.files[0]));
                $($recordingInfo).hide();
                $($questions).hide();
                $($resubmitButton).show();
            } else {
                FileValidator.fileChosenFailure();
            }
        });
    }
    
    $confirmationCheckbox.change(function(event) {
      if (this.checked) {
        $($submitButton).prop("disabled", false);
      } else {
        $($submitButton).prop("disabled", true);
      }
    })
    
    $resubmitButton.click(function (event) {
        event.preventDefault();

        if (confirm('Are you sure? Your previous video will be removed.')) {
            hideNode(selectionButtons);
            showNode(recordingButtons);
            hideNode(submitButton);
            hideNode(videoConfirmation);

            resetRecordedVideo();

            $($questions).show();
            $($recordingInfo).show();
            $($recordingTips).show();
            
            $('#video-source').attr('src', null);
            $($submitButton).prop("disabled", true);
            
            $confirmationCheckbox.prop("checked", false);

            $('#video-preview').get(0).pause();
            $('#video-preview').get(0).removeAttribute('src');
            $('#video-preview').get(0).load();
            $('#video-preview').hide();
            const pagePreview = document.querySelector('#page-preview');
            if (pagePreview) pagePreview.hidden = true;

            $videoFile.val('');

            $(this).hide();
        }
    });

    function validateForm(inputFields) {
        if (allFieldsAreValid(validateFields(inputFields))) {
            $($recordBtn).prop("disabled", false);
            $($jobApplicationVideoInput).prop("disabled", false);

            $uploadButton.removeClass("disabled");

            if (($('#video-source').attr('src') !== undefined) && ($('#video-source').attr('src').length > 0)) {
                $($submitButton).prop("disabled", false);
                $($resubmitButton).show();
            }
        } else {
            $($submitButton).prop("disabled", true);

            $($recordBtn).prop("disabled", true);
            $($jobApplicationVideoInput).prop("disabled", true);

            $uploadButton.addClass("disabled");

            if (($('#video-source').attr('src') !== undefined) && ($('#video-source').attr('src').length === 0)) {
                $($resubmitButton).hide();
            }
        }
    }

    function allFieldsAreValid(fields) {
        var result = fields.clone(true).off().filter(function () {
            return $(this).data('valid') !== true
        });

        return result.length === 0
    }

    function validateFields(fields) {
        var fieldsCopy = fields.clone(true).off();

        fieldsCopy.each(function () {
            if ($(this).val().trim().length > 0) {
                $(this).data('valid', true);
            } else {
                $(this).data('valid', false);
            }
        });

        return fieldsCopy;
    }

    validateForm($jobApplicationFormInputs);

    $jobApplicationFormInputs.keyup(function () {
        validateForm($jobApplicationFormInputs);
    });

    const FileValidator = {
        fileChosenFailure: function () {
            // No flair sadly
            $(".confetti").addClass("d-none");
            $('#recording-tips').show();
        },

        fileChosenSuccess: function (url) {
            const videoSource = document.querySelector('#video-source');
            const videoEl = videoSource && videoSource.parentElement;
            if (!videoSource || !videoEl) return;

            // Edit modal body to reflect a video was chosen
            const pagePreview = document.querySelector('#page-preview');
            if (pagePreview) pagePreview.hidden = false;
            $('#video-preview').show();

            $($recordingInfo).hide();
            $($recordingTips).hide();
            $($questions).hide();
            $($resubmitButton).show();
            showNode(submitButton);

            showNode(videoConfirmation);

            // Load video for preview
            videoSource.src = url;
            videoEl.load();
        },

        getExtension: function (filename) {
            return filename.split('.').pop();
        },

        isVideo: function (filename) {
            let fileExtension = this.getExtension(filename);
            switch (fileExtension.toLowerCase()) {
                case 'm4v':
                case 'avi':
                case 'mpg':
                case 'mp4':
                case 'mov':
                case 'webm':
                case 'ogg':
                case 'flv':
                case 'wmv':

                return true;
            }
            return false;
        }
    }

  window.addEventListener('fileLoadedLocally', (event) => {
    const { key, blob } = event.detail
    if (key !== 'jobApplication') return
    if (!blob) return
    if (!document.querySelector('#video-source')) return
    console.log('Processing locally loaded video...')

    let blobURL = URL.createObjectURL(blob);
    console.log(blobURL)
    FileValidator.fileChosenSuccess(blobURL);
    recordedBlobs = [blob];
  })

  const successSection = document.querySelector('#application-success');
  if (successSection) {
    deleteFileLocally('jobApplication');
  } else if (document.querySelector('#video-source')) {
    loadFileLocally('jobApplication');
  }

  const errorSection = document.querySelector('#error-container')
  if (errorSection) {
    const url = window.location.href
    if (url.endsWith('/edit')) return;

    const editUrl = url.concat('/edit')
    window.history.replaceState({}, document.title, editUrl)
  }
});
